import { fetchQuery } from 'react-relay';

import { IdentityUserAri } from '@atlassian/ari';
import { NavigationSettingAri } from '@atlassian/ari/navigation';
import { __INTERNAL_RELAY_ENVIRONMENT__DO_NOT_USE_THIS_DIRECTLY__ as platformEnv } from '@atlassian/relay-environment-provider/environment';
import GlobalSideNavigationAGGRelayConnectionQuery from '@atlassian/global-side-navigation/GlobalSideNavigationAGGRelayConnectionQuery.graphql';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { fg } from '@confluence/feature-gating';

// Page size values from get-page-size-value.relayprovider.ts
const DEFAULT_PAGE_SIZE = 100;

export const preloadPlatformNavigationQuery = async (): Promise<void> => {
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (!fg('navx-global-side-nav-confluence')) {
		return;
	}

	const { cloudId, activationId, userId } = getPreloaderFnContext();

	// Anonymous users have no userId, so there is no ownerAri to query with. Both remaining
	// selections (navPlatL1MenuData / navPlatSidebarData) are keyed on ownerAri, so an empty
	// one returns nothing usable — AGG rejects the request. fetchGlobalShortcutsAvailableProducts
	// is already false without an ownerAri, so nothing else is lost by skipping entirely.
	// PlatformNavigationComponent mirrors this check and renders GSN with `isAnonymous` (local
	// customization store, no queryRef) so the client never fires the query either.
	if (!cloudId || !activationId || !userId) {
		return;
	}

	const ownerAri = IdentityUserAri.create({ userId }).toString();

	const entityAriUserSideNavSettings = '';
	const fetchOldEntityRecord = false;

	// Use NavigationSettingAri to exactly match what buildNavigationAriForMenuData /
	// buildNavigationAriForSidebarData produce inside GlobalSideNavigationAGGRelayConnection.
	const navPlatMenuDataEntityAri = NavigationSettingAri.create({
		firstPartyApp: 'confluence',
		siteId: cloudId,
		activationId,
		settingId: 'nav-plat-l1-menu-data',
	}).toString();
	const navPlatSidebarDataEntityAri = NavigationSettingAri.create({
		firstPartyApp: 'confluence',
		siteId: cloudId,
		activationId,
		settingId: 'nav-plat-sidebar-data',
	}).toString();
	// The relay provider variable must match what GlobalSideNavigationAGGRelayConnectionQuery
	// sends at runtime — otherwise Relay's store lookup will miss.
	// See: get-page-size-value.relayprovider.ts
	const pageSizeValue = DEFAULT_PAGE_SIZE;

	const variables = {
		cloudId,
		// Must match RawGlobalSideNavigationAGGRelayConnection: entityAri is ConfluenceWorkspaceAri,
		// and fetchOldEntityRecord is true when entityAri is truthy.
		entityAriUserSideNavSettings,
		fetchGlobalShortcutsAvailableProducts: Boolean(cloudId && ownerAri),
		fetchOldEntityRecord,
		navPlatMenuDataEntityAri,
		navPlatSidebarDataEntityAri,
		ownerAri,
		__relay_internal__pv__getpagesizevaluerelayproviderts: pageSizeValue,
	};

	// On the server (Bifrost SSR context), await the AGG fetch so the Relay store is
	// populated before renderToPipeableStream begins. fetchQuery writes to the in-memory
	// store, which Bifrost serialises into __PLATFORM_SSR_AGG_CACHE__. On both server and
	// client, GlobalSideNavigationAGGRelayConnection reads from this store synchronously
	// via store-or-network fetchPolicy — no suspension, identical trees, no hydration mismatch.
	// On the client, this block is skipped — the store is already hydrated from the
	// serialised cache, so no second network request is made.
	// See: https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/356117
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		try {
			await fetchQuery(
				platformEnv,
				GlobalSideNavigationAGGRelayConnectionQuery,
				variables,
			).toPromise();
		} catch (e) {
			// fetchQuery may fail in SSR if platformEnv lacks auth credentials in the Bifrost
			// server context. Degrade gracefully — SSR will render GSN without preloaded data
			// and the client will fetch on hydration.
			// eslint-disable-next-line no-console
			console.warn('[GSN preloader] fetchQuery failed in SSR context:', e);
		}
	}
};
